import React, { useState } from 'react';
import logo from '../images/logo.png'; // Adjust the path based on your project structure

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { name: 'Home', href: '#home' },
    { name: 'Features', href: '#features' },
    { name: 'Why Choose Us', href: '#why-choose' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  // Pharmacy floating icons
  const pharmacyFloatingIcons = [
    { icon: '💊', size: 'w-6 h-6', position: 'top-4 left-20', delay: 'delay-0' },
    { icon: '🏪', size: 'w-5 h-5', position: 'top-6 right-32', delay: 'delay-1000' },
    { icon: '🚚', size: 'w-5 h-5', position: 'bottom-4 left-32', delay: 'delay-2000' },
    { icon: '📦', size: 'w-4 h-4', position: 'bottom-6 right-24', delay: 'delay-1500' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="bg-gradient-to-br from-indigo-900 via-blue-800 to-purple-900 backdrop-blur-lg border-b border-white/20 fixed w-full z-50 h-20">
      {/* Floating Pharmacy Icons in Navbar */}
      {pharmacyFloatingIcons.map((item, index) => (
        <div
          key={index}
          className={`absolute ${item.position} ${item.size} bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 flex items-center justify-center text-xs animate-float-slow ${item.delay} z-30`}
        >
          {item.icon}
        </div>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex justify-between items-center h-full">
          {/* Left Side - Logo with Image */}
          <div className="flex items-center">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-xl blur-md opacity-70 animate-gradient-x"></div>
                <div className="relative w-12 h-12 rounded-xl bg-white p-1 animate-float">
                  <img 
                    src={logo} 
                    alt="Clynix Pharmacy Logo"
                    className="w-full h-full object-contain animate-gentle-scale"
                  />
                  <div className="absolute inset-0 border-2 border-cyan-400 rounded-xl animate-ping-slow opacity-30"></div>
                </div>
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-cyan-400 rounded-full animate-ping"></div>
                <div className="absolute -bottom-1 -left-1 w-1 h-1 bg-purple-400 rounded-full animate-ping delay-1000"></div>
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent animate-gradient-x">
                CLYNIX
              </span>
            </div>
          </div>

          {/* Center - Navigation Items */}
          <div className="hidden md:flex items-center space-x-10">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.href)}
                className="text-gray-300 hover:text-white font-semibold transition-colors duration-200 relative group"
              >
                {item.name}
                <span className="absolute -bottom-2 left-0 w-0 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 transition-all duration-300 group-hover:w-full rounded-full animate-gradient-x"></span>
              </button>
            ))}
          </div>

          {/* Right Side - Contact Number */}
          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex items-center space-x-3">
              <div className="bg-white/10 backdrop-blur-md border border-cyan-400/30 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 hover:bg-white/20 hover:scale-105">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    +91 8309056333
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-3 rounded-xl text-gray-300 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500 transition-all duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-white/20 mt-2">
            <div className="px-2 pt-4 pb-4 space-y-3 bg-white/10 backdrop-blur-lg rounded-xl mt-2 border border-white/20">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollToSection(item.href)}
                  className="text-gray-300 hover:text-white block px-4 py-3 rounded-lg text-base font-semibold transition-colors duration-200 hover:bg-white/10 w-full text-left relative group"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-4 right-4 h-0.5 bg-gradient-to-r from-cyan-400 to-purple-500 transition-all duration-300 group-hover:w-full rounded-full animate-gradient-x"></span>
                </button>
              ))}
              <div className="pt-3">
                <div className="flex justify-center items-center bg-white/10 backdrop-blur-md border border-cyan-400/30 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300">
                  <svg className="w-5 h-5 mr-2 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                    +1 (555) 123-4567
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-2px) rotate(1deg); }
          66% { transform: translateY(1px) rotate(-0.5deg); }
        }
        @keyframes gentle-scale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.8; }
        }
        @keyframes ping-slow {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.3); opacity: 0; }
        }
        @keyframes gradient-x {
          0%, 100% {
            background-size: 200% 200%;
            background-position: left center;
          }
          50% {
            background-size: 200% 200%;
            background-position: right center;
          }
        }
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          33% { 
            transform: translateY(-4px) rotate(2deg); 
          }
          66% { 
            transform: translateY(2px) rotate(-1deg); 
          }
        }
        .animate-float { animation: float 8s ease-in-out infinite; }
        .animate-gentle-scale { animation: gentle-scale 5s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
        .animate-ping-slow { animation: ping-slow 3s cubic-bezier(0, 0, 0.2, 1) infinite; }
        .animate-gradient-x { 
          animation: gradient-x 3s ease infinite; 
          background-size: 200% 200%;
        }
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
      `}</style>
    </nav>
  );
};

export default Navbar;